// src/services/hooks/useKanjiSearch.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { getToken } from '../../config/firebase';
import { usePostgresUser } from '../../components/utils/PostgresUserContext';
import { backendService } from '../api/backendService';

/**
 * useKanjiSearch
 * - Debounced, cancelable searches using AbortController
 * - Waits for auth / uses getIdToken()
 * - Persist query/results to sessionStorage
 * - Exposes manualSearch() to run search immediate (Enter)
 *
 * API:
 * const {
 *   results,
 *   isLoading,
 *   error,
 *   query,
 *   setQuery,
 *   clear,
 *   manualSearch,
 *   filter,
 *   setFilter
 * } = useKanjiSearch(initialQuery, { debounceMs, minChars, limit })
 */
export const useKanjiSearch = (
  initialQuery = '',
  { debounceMs = 300, minChars = 2, limit = 20 } = {}
) => {
  const { user, loading: authLoading } = usePostgresUser();

  // session storage keys
  const QKEY = 'allKanjiSearchQuery';
  const RKEY = 'allKanjiSearchResults';
  const FKEY = 'allKanjiSearchFilter';

  const [query, setQuery] = useState(() => {
    try {
      const saved = sessionStorage.getItem(QKEY);
      return initialQuery || saved || '';
    } catch {
      return initialQuery || '';
    }
  });

  const [filter, setFilter] = useState(() => {
    try {
      return sessionStorage.getItem(FKEY) || 'all';
    } catch {
      return 'all';
    }
  });

  const [results, setResults] = useState(() => {
    try {
      const savedRes = sessionStorage.getItem(RKEY);
      return savedRes ? JSON.parse(savedRes) : null;
    } catch {
      return null;
    }
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // refs
  const mountedRef = useRef(true);
  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  const getIdToken = useCallback(() => {
    return getToken(); // Returns null if not logged in (public access OK)
  }, []);

  // build final search string depending on filter selection
  const buildEffectiveQuery = useCallback((q, f) => {
    // if filter is something like 'jlpt-n5', prefix with #jlpt-n5 the same style as Jisho
    if (!q || q.trim().length === 0) {
      // allow searching only by filter (e.g. '#jlpt-n5')
      return f && f !== 'all' ? `#${f}` : '';
    }
    if (f && f !== 'all') {
      // If user already typed '#jlpt', don't double prefix
      if (q.includes(`#${f}`)) return q;
      return `#${f} ${q}`.trim();
    }
    return q;
  }, []);

  const performSearch = useCallback(
    async (rawQuery, rawFilter, { bypassAuth = false } = {}) => {
      const effectiveQuery = buildEffectiveQuery(rawQuery || '', rawFilter || filter);

      // Clear previous abort
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      // validate length
      if (!effectiveQuery || effectiveQuery.replace(/#\S+\s*/, '').trim().length < minChars) {
        // too short (or only filter without chars is allowed - backend may return all for filter)
        // we'll treat this as "no results" unless only filter present (e.g. #jlpt-n5)
        if (!mountedRef.current) return;

        // If only filter and it's allowed, still perform call (we let backend handle).
        // But if effectiveQuery effectively empty, reset.
        const stripped = effectiveQuery.replace(/#\S+\s*/, '').trim();
        if (!stripped) {
          setResults(null);
          setError(null);
          setIsLoading(false);
          try {
            sessionStorage.setItem(QKEY, rawQuery || '');
            sessionStorage.removeItem(RKEY);
            sessionStorage.setItem(FKEY, rawFilter || 'all');
          } catch {}
          return;
        }
      }

      setIsLoading(true);
      setError(null);

      try {
        // Wait for auth to settle if still loading
        if (authLoading) {
          await new Promise((res) => {
            const check = () => {
              if (!authLoading) return res();
              setTimeout(check, 50);
            };
            check();
          });
        }

        const idToken = await getIdToken();
        // backendService.searchKanji should accept query, limit, idToken
        const data = await backendService.searchKanji(effectiveQuery.trim(), limit, idToken, { signal });
        const normalized = data?.results ?? data ?? [];

        if (!mountedRef.current) return;
        setResults(normalized);

        // persist
        try {
          sessionStorage.setItem(RKEY, JSON.stringify(normalized));
          sessionStorage.setItem(QKEY, rawQuery || '');
          sessionStorage.setItem(FKEY, rawFilter || 'all');
        } catch {}
      } catch (err) {
        if (err?.name === 'AbortError') {
          // aborted — ignore
          return;
        }
        console.error('Error searching kanji:', err);
        if (!mountedRef.current) return;
        setError(err.message || 'Search failed');
        setResults(null);
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    },
    [buildEffectiveQuery, filter, getIdToken, authLoading, limit, minChars]
  );

  // Debounced effect when query or filter changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Always persist the raw query immediately
    try {
      sessionStorage.setItem(QKEY, query || '');
      sessionStorage.setItem(FKEY, filter || 'all');
    } catch {}

    // If query is too short but filter exists, we may still want to run search (e.g., "#jlpt-n5")
    const effective = buildEffectiveQuery(query, filter);
    const stripped = effective.replace(/#\S+\s*/, '').trim();

    if (!effective || (stripped.length < minChars && stripped.length !== 0)) {
      // schedule quick cleanup to avoid flash
      debounceRef.current = setTimeout(() => {
        if (!mountedRef.current) return;
        setResults(null);
        setIsLoading(false);
        setError(null);
        // clear stored results if too short
        try {
          sessionStorage.removeItem(RKEY);
        } catch {}
      }, debounceMs);
      return;
    }

    debounceRef.current = setTimeout(() => {
      performSearch(query, filter);
    }, debounceMs);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      // don't abort here; allow next call to abort previous via performSearch
    };
  }, [query, filter, debounceMs, minChars, performSearch, buildEffectiveQuery]);

  // manual search for immediate execution (e.g. Enter)
  const manualSearch = useCallback(
    (overrideQuery = null, overrideFilter = null) => {
      // abort pending debounced timer and run immediately
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      performSearch(overrideQuery ?? query, overrideFilter ?? filter);
    },
    [performSearch, query, filter]
  );

  const clear = useCallback(() => {
    setQuery('');
    setResults(null);
    setError(null);
    try {
      sessionStorage.removeItem(QKEY);
      sessionStorage.removeItem(RKEY);
      sessionStorage.removeItem(FKEY);
    } catch {}
  }, []);

  return {
    results,
    isLoading,
    error,
    query,
    setQuery,
    clear,
    manualSearch,
    filter,
    setFilter,
  };
};
